'use es6';

import I18n from 'I18n';
import { Map as ImmutableMap } from 'immutable';
import { Fragment } from 'react';
import { renderToString } from 'react-dom/server';
import { getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import { HR } from 'foundations-components/transitional/components/HR';
import { getBreakdownField, getBucketField, getMetricField } from '../lib/salesAnalytics/helpers';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Box } from 'foundations-components/transitional/components/Box';
import styled from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledSeriesSymbol = styled.div.withConfig({
  displayName: "tooltip__StyledSeriesSymbol",
  componentId: "sc-1i9cxdb-0"
})(["border-radius:50%;width:", ";height:", ";margin-right:", ";"], ({
  theme
}) => theme.space['200'], ({
  theme
}) => theme.space['200'], ({
  theme
}) => theme.space['100']);
const getBucketKeyVal = (dataset, property, xLabel) => {
  return dataset.getIn(['properties', property, 'references']).findKey(ref => ref.get('label') === xLabel);
};
const getDataList = (data, bucketProperty, bucketValue) => {
  return data.get('data').filter(point => point.get(bucketProperty) === bucketValue) || [];
};
const renderTooltipHeader = label => /*#__PURE__*/_jsx(View, {
  color: "fill-surface-default",
  backgroundColor: "fill-accent-neutral-default",
  padding: "200",
  borderStartStartRadius: "100",
  borderStartEndRadius: "100",
  children: label
});
const renderBasicTooltipBody = (dataset, row, name) => {
  const bucketKey = dataset.get('properties').findKey(prop => prop.get('label') === name);
  const amountVal = row.get(bucketKey);
  const amountLabel = getReferenceLabel(dataset, bucketKey, amountVal);
  const groupLabel = I18n.text('reporting-ui-components.tooltip.group', {
    group: I18n.SafeString(name)
  });
  return amountVal ? /*#__PURE__*/_jsxs(Flex, {
    children: [/*#__PURE__*/_jsx(Flex, {
      flexGrow: 1,
      children: /*#__PURE__*/_jsx(Text, {
        as: "span",
        color: "fill-accent-neutral-default",
        typography: "heading-25",
        children: groupLabel
      })
    }), /*#__PURE__*/_jsx(Box, {
      paddingInlineStart: "100",
      alignSelf: "flex-end",
      children: /*#__PURE__*/_jsx(Text, {
        as: "span",
        color: "text-core-subtle",
        typography: "heading-25",
        children: amountLabel
      })
    })]
  }) : null;
};
const renderGroupedTooltipBody = (dataList, primaryData, bucket, groupingVal, amountField, colors) => {
  return dataList.map((dataPoint, i) => {
    const rowLabel = getReferenceLabel(primaryData, bucket, dataPoint.get(bucket));
    const groupLabel = I18n.text('reporting-ui-components.tooltip.group', {
      group: I18n.SafeString(rowLabel)
    });
    const amountVal = dataPoint.get(`SUM|${amountField}`, 0);
    const amountLabel = getReferenceLabel(primaryData, `SUM|${amountField}`, amountVal);
    const attainmentPercentageVal = dataPoint.get('attainmentPercentage', 0);
    const attainmentPercentLabel = getReferenceLabel(primaryData, 'attainmentPercentage', attainmentPercentageVal);
    const attainmentLabel = I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.attainmentPercentage.tooltip', {
      percentage: attainmentPercentLabel
    });
    return amountVal || attainmentPercentageVal ? /*#__PURE__*/_jsxs(Flex, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexGrow: 1,
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(StyledSeriesSymbol, {
          style: {
            backgroundColor: colors[rowLabel]
          }
        }), /*#__PURE__*/_jsx(Text, {
          color: "fill-accent-neutral-default",
          typography: !groupingVal || groupingVal === dataPoint.get(bucket) ? 'heading-25' : 'detail-200',
          children: groupLabel
        })]
      }), /*#__PURE__*/_jsx(Box, {
        paddingInlineStart: "100",
        alignSelf: "flex-end",
        children: /*#__PURE__*/_jsx(Text, {
          as: "span",
          color: "text-core-subtle",
          typography: "heading-25",
          children: `${amountLabel} (${attainmentLabel})`
        })
      })]
    }, i) : null;
  });
};
const renderTooltipTotal = (dataMap, quotaData, amountField) => {
  if (!dataMap.get(`SUM|${amountField}`)) {
    return null;
  }
  const amountLabel = getReferenceLabel(quotaData, `SUM|${amountField}`, dataMap.get(`SUM|${amountField}`));
  const attainmentPercentLabel = getReferenceLabel(quotaData, 'attainmentPercentage', dataMap.get('attainmentPercentage'));
  const attainmentLabel = I18n.text('reporting-ui-components.customWidgets.salesQuota.properties.attainmentPercentage.tooltip', {
    percentage: attainmentPercentLabel
  });
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    }, "hr"), /*#__PURE__*/_jsxs(Flex, {
      children: [/*#__PURE__*/_jsx(Flex, {
        flexGrow: 1,
        children: /*#__PURE__*/_jsx(Text, {
          as: "span",
          color: "fill-accent-neutral-default",
          typography: "detail-200",
          marginInlineStart: "300",
          children: I18n.text('reporting-ui-components.tooltip.totals')
        })
      }), /*#__PURE__*/_jsx(Box, {
        paddingInlineStart: "100",
        alignSelf: "flex-end",
        children: /*#__PURE__*/_jsx(Text, {
          as: "span",
          color: "text-core-subtle",
          typography: "heading-25",
          children: `${amountLabel} (${attainmentLabel})`
        })
      })]
    }, "totalsRow")]
  });
};
export const tooltipFormatter = ({
  report,
  datasets
}) => function () {
  const {
    series: {
      name,
      chart: {
        series
      }
    },
    x
  } = this;
  const primaryData = datasets.getIn(['primary', 'data']);
  const quotaData = datasets.getIn(['quota', 'data']);
  const bucketProperty = getBucketField(report);
  const grouping = getBreakdownField(report);
  const amountField = getMetricField(report);
  const bucketVal = getBucketKeyVal(primaryData, bucketProperty, x);
  const groupingVal = grouping ? getBucketKeyVal(primaryData, grouping, name) : null;
  const primaryDataList = getDataList(primaryData, bucketProperty, bucketVal);
  const quotaDataMap = getDataList(quotaData, bucketProperty, bucketVal).get(0) || ImmutableMap();
  const colorMap = series.reduce((acc, seriesPoint) => {
    return Object.assign({}, acc, {
      [seriesPoint.name]: seriesPoint.color
    });
  }, {});
  const tooltip = /*#__PURE__*/_jsxs(View, {
    minInlineSize: "150px",
    backgroundColor: "fill-surface-default",
    boxShadow: "300",
    children: [renderTooltipHeader(x), /*#__PURE__*/_jsxs(View, {
      padding: "200",
      color: "fill-accent-neutral-default",
      backgroundColor: "fill-surface-default",
      children: [groupingVal ? renderGroupedTooltipBody(primaryDataList, primaryData, grouping, groupingVal, amountField, colorMap) : renderBasicTooltipBody(quotaData, quotaDataMap, name), renderTooltipTotal(quotaDataMap, quotaData, amountField)]
    })]
  });
  return renderToString(tooltip);
};